import { Component, OnInit, ElementRef, HostListener, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

interface CaptchaImage {
  id: number;
  url: string;
  isFadingOut: boolean;
  isFadingIn: boolean;
  visible: boolean;
}

@Component({
  selector: 'app-fake-captcha',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fake-captcha.component.html',
  styleUrls: ['./fake-captcha.component.scss']
})
export class FakeCaptchaComponent implements OnInit {
  @Output() success = new EventEmitter<'magic' | 'random' | void>();

  isChecked = false;
  isModalOpen = false;
  isFailed = false;
  isCheckboxLoading = false;
  isVerifying = false;
  
  challengeWord = '';
  images: CaptchaImage[] = [];
  
  private words = ['cars', 'traffic signs', 'bridges', 'traffic lights', 'bicycles', 'stop signs', 'buses', 'objects bigger than 1ft', 'trees', "hydrants"];
  private totalImages = 153;

  constructor(private eRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (this.isModalOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.closeModal();
    }
  }

  ngOnInit() {
    this.resetChallenge();
  }

  toggleCheck() {
    if (this.isChecked || this.isCheckboxLoading) return;
    
    // If failed, we reset and start over
    if (this.isFailed) {
      this.isFailed = false;
    }
    
    this.isCheckboxLoading = true;
    
    // Simulate network delay
    setTimeout(() => {
      this.isCheckboxLoading = false;
      this.openModal();
    }, 1000);
  }

  openModal() {
    this.isModalOpen = true;
    this.resetChallenge();
  }

  closeModal() {
    this.isModalOpen = false;
    this.isVerifying = false;
    // If we closed without verifying (clicking outside), we essentially reset to start state (unchecked)
    // unless it was already checked, but here we assume click outside cancels the flow.
    // However, if it was ALREADY checked, we probably shouldn't be opening the modal anyway?
    // The requirement says "Clicking outside the captcha window should reset it."
    // Implies "reset to unchecked/initial state".
    this.isChecked = false;
    this.isCheckboxLoading = false;
  }

  resetChallenge() {
    this.challengeWord = this.words[Math.floor(Math.random() * this.words.length)];
    this.images = Array(9).fill(0).map((_, i) => this.createNewImage(i));
  }

  createNewImage(id: number): CaptchaImage {
    const imageNum = Math.floor(Math.random() * this.totalImages) + 1;
    return {
      id,
      url: `/img/captcha/${imageNum}.png`,
      isFadingOut: false,
      isFadingIn: false,
      visible: true
    };
  }

  fadeDuration = 0.5;

  onImageClick(image: CaptchaImage) {
    if (image.isFadingOut || !image.visible) return;

    // Start fade out
    image.isFadingOut = true;
    
    // Logic: Fade out -> Wait -> Replace URL -> Fade in
    setTimeout(() => {
      image.visible = false;
      image.isFadingOut = false;
      
      // Wait a bit before loading new one (mimic network/processing)
      setTimeout(() => {
        const imageNum = Math.floor(Math.random() * this.totalImages) + 1;
        image.url = `/img/captcha/${imageNum}.png`;
        image.visible = true;
        image.isFadingIn = true;
        
        // Remove fade in class after animation
        setTimeout(() => {
          image.isFadingIn = false;
        }, this.fadeDuration * 1000); 
      }, 200);
    }, this.fadeDuration * 1000);

    // Increase duration for next time
    this.fadeDuration += 0.1;
  }

  onContinue() {
    this.isVerifying = true;

    setTimeout(() => {
      // Check localStorage for magic key
      if (localStorage.getItem('captcha') === '1') {
        this.isChecked = true;
        this.isModalOpen = false;
        this.isVerifying = false;
        this.success.emit('magic');
        return;
      }

      // 1% chance of success (Rigged)
      if (Math.random() < 0.05) {
        this.isChecked = true;
        this.isModalOpen = false;
        this.isVerifying = false;
        this.success.emit('random');
        return;
      }

      // 10% chance of failure
      if (Math.random() < 0.1) {
        this.handleFailure();
        return;
      }

      // 90% chance to just refresh (infinite loop)
      this.resetChallenge();
      this.isVerifying = false;
    }, 500);
  }

  handleFailure() {
    this.isVerifying = false;
    this.isModalOpen = false;
    this.isFailed = true;
    this.isChecked = false;
  }

  reset() {
    this.isChecked = false;
    this.isFailed = false;
    this.isVerifying = false;
    this.isModalOpen = false;
  }
}
